import React, {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowRight,
  ChevronDown,
  RotateCcw,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import {
  ClinicianAssistantContext,
  ClinicianAssistantEvidence,
  ClinicianAssistantLink,
  ClinicianAssistantResult,
  ClinicianAssistantScope,
} from "../api/contracts";
import {
  clinicianPortalApi,
  ClinicianPortalApiError,
} from "../api/clinicianPortalApi";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import {
  clinicianAssistantContextForRoute,
  clinicianAssistantContextLabel,
  clinicianAssistantStarters,
  pathForClinicianAssistantLink,
} from "../routing/clinicianAssistantRouting";
import { ClinicianPortalRoute } from "../routing/portalRoutes";
import { usePortalData } from "../hooks/usePortalData";
import { formatPortalDate, formatPortalDateTime } from "../utils/dateTime";
import {
  ClinicianAssistantController,
  ClinicianAssistantControllerContext,
  ClinicianAssistantTeamScopeFilters,
} from "./ClinicianAssistantTrigger";
import { PortalLink } from "./PortalLink";

interface AssistantMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  result?: ClinicianAssistantResult;
  originalQuestion?: string;
}

interface FailedRequest {
  question: string;
  context: ClinicianAssistantContext;
  appendUser: boolean;
}

interface ExampleQuestionGroup {
  title: string;
  description?: string;
  questions: string[];
}

const clientNicknamePlaceholder = "[client nickname]";

const namedClientExampleQuestions = [
  `How is ${clientNicknamePlaceholder} doing?`,
  `What was ${clientNicknamePlaceholder}'s last completed homework?`,
  `What did ${clientNicknamePlaceholder} complete most recently?`,
  `What homework does ${clientNicknamePlaceholder} have now?`,
  `Does ${clientNicknamePlaceholder} have anything due?`,
  `Does ${clientNicknamePlaceholder} have overdue homework?`,
  `How many overdue assignments does ${clientNicknamePlaceholder} have?`,
  `What was ${clientNicknamePlaceholder}'s latest mood check?`,
  `Show ${clientNicknamePlaceholder}'s recent mood ratings.`,
  `Has ${clientNicknamePlaceholder} had any recent safety events?`,
  `Summarize ${clientNicknamePlaceholder}'s recent homework activity.`,
];

const currentClientExampleQuestions = [
  "How is this client doing?",
  "What was this client's last completed homework?",
  "What did this client complete most recently?",
  "What homework does this client have now?",
  "Does this client have anything due?",
  "Does this client have overdue homework?",
  "How many overdue assignments does this client have?",
  "What was this client's latest mood check?",
  "Show this client's recent mood ratings.",
  "Has this client had any recent safety events?",
  "Summarize this client's recent homework activity.",
];

const caseloadExampleQuestions = [
  "Which clients need my attention?",
  "Who needs follow-up?",
  "Which clients have no homework assigned?",
  "Which clients have low homework completion?",
  "Which clients are on track?",
  "Which clients had recent safety events?",
  "Who has not interacted with the assistant in 7 days?",
];

const teamClientExampleQuestions = [
  `How is ${clientNicknamePlaceholder} doing?`,
  `What was ${clientNicknamePlaceholder}'s last completed homework?`,
  `Does ${clientNicknamePlaceholder} have overdue homework?`,
  `Summarize ${clientNicknamePlaceholder}'s recent homework activity.`,
];

const teamHomeworkExampleQuestions = [
  "Who completed homework on an iPhone?",
  "Who took the longest to complete homework?",
  "Which clients completed homework most recently?",
  "Which device was used most often to complete homework?",
  "Which clients attempted homework but did not complete it?",
];

const teamTherapistExampleQuestions = [
  "Compare homework completion across therapists.",
  "Which therapists' clients completed the most homework?",
  "Which therapist's clients used the assistant most this week?",
];

const emptyTeamScopeFilters: ClinicianAssistantTeamScopeFilters = {
  clinicId: "",
  therapistId: "",
};

function formatAssistantTemporal(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
    ? formatPortalDate(value)
    : formatPortalDateTime(value);
}

function friendlyAssistantError(error: unknown): string {
  if (error instanceof ClinicianPortalApiError) {
    if (error.code === "NOT_AUTHORIZED") {
      return "You do not have access to the requested clinician or client information.";
    }
    if (error.code === "TIMEOUT") {
      return "The assistant took too long to respond. Please try again.";
    }
    if (error.code === "NETWORK_ERROR") {
      return "The assistant could not be reached. Check your connection and try again.";
    }
  }
  return "The assistant could not complete that request. Please try again.";
}

function AssistantMarkdown({ children }: { children: string }) {
  return (
    <div className="cp-clinician-assistant__markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeSanitize]}
        skipHtml
        components={{
          h1: ({ children: heading }) => <h3>{heading}</h3>,
          h2: ({ children: heading }) => <h3>{heading}</h3>,
          h3: ({ children: heading }) => <h4>{heading}</h4>,
          a: ({ children: linkText }) => <span>{linkText}</span>,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

function isDiagnosticEvidenceValue(value: unknown): boolean {
  return (
    typeof value === "string" &&
    /(?:^|;\s*)[A-Za-z][A-Za-z0-9_]*\s*=/.test(value)
  );
}

function EvidenceCard({ evidence }: { evidence: ClinicianAssistantEvidence }) {
  const showValue =
    evidence.value !== undefined &&
    evidence.value !== null &&
    evidence.value !== "" &&
    !isDiagnosticEvidenceValue(evidence.value);

  return (
    <li className="cp-clinician-assistant__evidence-card">
      <span className="cp-clinician-assistant__evidence-label">
        {evidence.label}
      </span>
      {showValue ? (
        <strong className="cp-clinician-assistant__evidence-value">
          {evidence.value}
        </strong>
      ) : null}
      {evidence.occurredAt ? (
        <time dateTime={evidence.occurredAt}>
          {formatAssistantTemporal(evidence.occurredAt)}
        </time>
      ) : null}
    </li>
  );
}

function StructuredLink({
  link,
  route,
  clinicianCode,
}: {
  link: ClinicianAssistantLink;
  route: ClinicianPortalRoute;
  clinicianCode: string;
}) {
  const path = pathForClinicianAssistantLink(link, route);
  if (!path) return null;
  return (
    <PortalLink
      className="cp-clinician-assistant__record-link"
      path={path}
      clinicianCode={clinicianCode}
    >
      {link.label}
      <ArrowRight aria-hidden="true" />
    </PortalLink>
  );
}

export function ClinicianAssistantProvider({
  clinicianCode,
  route,
  children,
}: {
  clinicianCode: string;
  route: ClinicianPortalRoute;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedRequest, setFailedRequest] = useState<FailedRequest | null>(null);
  const [teamAccessForbidden, setTeamAccessForbidden] = useState(false);
  const [resolvedNickname, setResolvedNickname] = useState<string>();
  const [scopeMode, setScopeMode] =
    useState<ClinicianAssistantScope["mode"]>("personal");
  const [teamScopeFilters, setTeamScopeFilters] =
    useState<ClinicianAssistantTeamScopeFilters>(emptyTeamScopeFilters);
  const nextMessageId = useRef(1);
  const requestController = useRef<AbortController>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const conversationRef = useRef<HTMLDivElement>(null);
  const shouldSelectClientNicknamePlaceholder = useRef(false);
  const shouldScrollAfterUserMessage = useRef(false);
  const previousPatientId = useRef<string | undefined>();
  const hasObservedPatientContext = useRef(false);
  const previousScopeSignature = useRef<string>();
  const previousIdentitySignature = useRef<string>();
  const hasExplicitScopeSelection = useRef(false);

  const identityRequest = useCallback(
    (signal: AbortSignal) => clinicianPortalApi.getMyClients(clinicianCode, signal),
    [clinicianCode],
  );
  const identityState = usePortalData(identityRequest);
  const identityData =
    identityState.status === "success" || identityState.status === "refreshing"
      ? identityState.data
      : undefined;
  const requestingTherapist = identityData?.found
    ? identityData.therapist
    : null;
  const identityResolved = identityState.status !== "loading";
  const isSupervisor = requestingTherapist?.role === "supervisor";
  const effectiveScopeMode =
    scopeMode === "team" && isSupervisor ? "team" : "personal";
  const draftNeedsClientNickname = draft.includes(clientNicknamePlaceholder);

  const cachedNickname =
    "patientId" in route
      ? getClinicianPortalClientSnapshot(
          route.patientId,
          "clientSource" in route && route.clientSource === "team"
            ? "team"
            : "assigned",
        )?.patient.nickname
      : undefined;
  const context = useMemo(
    () =>
      clinicianAssistantContextForRoute(
        route,
        cachedNickname ?? resolvedNickname,
      ),
    [cachedNickname, resolvedNickname, route],
  );
  const contextLabel = clinicianAssistantContextLabel(context);
  const starters = useMemo(
    () => clinicianAssistantStarters(context, effectiveScopeMode),
    [context, effectiveScopeMode],
  );
  const exampleQuestionGroups = useMemo<ExampleQuestionGroup[]>(
    () =>
      effectiveScopeMode === "team"
        ? [
            {
              title: "Specific team client",
              description:
                "Choose a question, then replace [client nickname] with an authorized team client's nickname before sending.",
              questions: teamClientExampleQuestions,
            },
            {
              title: "Homework and devices",
              questions: teamHomeworkExampleQuestions,
            },
            {
              title: "Therapists and caseloads",
              questions: teamTherapistExampleQuestions,
            },
          ]
        : [
            context.patientId
              ? {
                  title: "This client",
                  questions: currentClientExampleQuestions,
                }
              : {
                  title: "About a client",
                  description:
                    "Choose a question, then replace [client nickname] with an authorized client's nickname before sending.",
                  questions: namedClientExampleQuestions,
                },
            {
              title: "My caseload",
              questions: caseloadExampleQuestions,
            },
          ],
    [context.patientId, effectiveScopeMode],
  );

  const assistantScope = useMemo<ClinicianAssistantScope>(
    () =>
      effectiveScopeMode === "team"
        ? {
            mode: "team",
            clinicId: teamScopeFilters.clinicId,
            therapistId: teamScopeFilters.therapistId,
          }
        : { mode: "personal" },
    [effectiveScopeMode, teamScopeFilters.clinicId, teamScopeFilters.therapistId],
  );

  const clearConversation = useCallback(() => {
    requestController.current?.abort();
    requestController.current = undefined;
    setMessages([]);
    setDraft("");
    setShowExamples(false);
    setError(null);
    setFailedRequest(null);
    setTeamAccessForbidden(false);
    setIsSubmitting(false);
  }, []);

  useEffect(() => {
    if (
      hasObservedPatientContext.current &&
      previousPatientId.current !== context.patientId
    ) {
      clearConversation();
      setResolvedNickname(undefined);
    }
    hasObservedPatientContext.current = true;
    previousPatientId.current = context.patientId;
  }, [clearConversation, context.patientId]);

  useEffect(() => {
    if (!identityResolved) return;
    const identitySignature = `${clinicianCode}:${requestingTherapist?.id ?? ""}:${requestingTherapist?.role ?? ""}`;
    if (
      previousIdentitySignature.current !== undefined &&
      previousIdentitySignature.current !== identitySignature
    ) {
      clearConversation();
      hasExplicitScopeSelection.current = false;
    }
    previousIdentitySignature.current = identitySignature;

    if (!hasExplicitScopeSelection.current) {
      const defaultMode =
        isSupervisor && (route.kind === "home" || route.globalKey === "team")
          ? "team"
          : "personal";
      setScopeMode((current) => (current === defaultMode ? current : defaultMode));
    } else if (!isSupervisor && scopeMode === "team") {
      setScopeMode("personal");
    }
  }, [
    clearConversation,
    clinicianCode,
    identityResolved,
    isSupervisor,
    requestingTherapist?.id,
    requestingTherapist?.role,
    route.globalKey,
    route.kind,
    scopeMode,
  ]);

  useEffect(() => {
    const scopeSignature = `${effectiveScopeMode}:${teamScopeFilters.clinicId}:${teamScopeFilters.therapistId}`;
    if (
      previousScopeSignature.current !== undefined &&
      previousScopeSignature.current !== scopeSignature
    ) {
      clearConversation();
    }
    previousScopeSignature.current = scopeSignature;
  }, [
    clearConversation,
    effectiveScopeMode,
    teamScopeFilters.clinicId,
    teamScopeFilters.therapistId,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (showExamples) {
          setShowExamples(false);
          return;
        }
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, showExamples]);

  useEffect(
    () => () => {
      requestController.current?.abort();
    },
    [],
  );

  useEffect(() => {
    if (!shouldScrollAfterUserMessage.current) return;

    shouldScrollAfterUserMessage.current = false;
    const conversation = conversationRef.current;
    if (!conversation) return;

    if (typeof conversation.scrollTo === "function") {
      conversation.scrollTo({
        top: conversation.scrollHeight,
        behavior: "smooth",
      });
    } else {
      conversation.scrollTop = conversation.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    if (!shouldSelectClientNicknamePlaceholder.current) return;

    shouldSelectClientNicknamePlaceholder.current = false;
    const placeholderStart = draft.indexOf(clientNicknamePlaceholder);
    const composer = composerRef.current;
    if (placeholderStart < 0 || !composer) return;

    composer.focus();
    composer.setSelectionRange(
      placeholderStart,
      placeholderStart + clientNicknamePlaceholder.length,
    );
  }, [draft]);

  const sendQuestion = useCallback(
    async (
      question: string,
      requestContext: ClinicianAssistantContext = context,
      appendUser = true,
    ) => {
      const normalizedQuestion = question.trim();
      if (!normalizedQuestion || isSubmitting) return;
      if (normalizedQuestion.includes(clientNicknamePlaceholder)) {
        const placeholderStart = question.indexOf(clientNicknamePlaceholder);
        composerRef.current?.focus();
        composerRef.current?.setSelectionRange(
          placeholderStart,
          placeholderStart + clientNicknamePlaceholder.length,
        );
        return;
      }

      const history = messages
        .map(({ role, content }) => ({ role, content }))
        .slice(-10);
      if (appendUser) {
        shouldScrollAfterUserMessage.current = true;
        setMessages((current) => [
          ...current,
          {
            id: nextMessageId.current++,
            role: "user",
            content: normalizedQuestion,
          },
        ]);
      }
      setDraft("");
      setError(null);
      setFailedRequest(null);
      setIsSubmitting(true);
      requestController.current?.abort();
      const controller = new AbortController();
      requestController.current = controller;

      try {
        const result = await clinicianPortalApi.askClinicianAssistant(
          clinicianCode,
          normalizedQuestion,
          requestContext,
          history,
          {
            scope: assistantScope,
            ...(requestingTherapist?.id
              ? { requestingTherapistId: requestingTherapist.id }
              : {}),
            signal: controller.signal,
          },
        );
        if (controller.signal.aborted) return;
        const content =
          "needsClarification" in result && result.needsClarification
            ? result.message
            : result.answer;
        setMessages((current) => [
          ...current,
          {
            id: nextMessageId.current++,
            role: "assistant",
            content,
            result,
            originalQuestion: normalizedQuestion,
          },
        ]);
        if (
          !("needsClarification" in result && result.needsClarification) &&
          result.resolvedContext?.patientNickname
        ) {
          setResolvedNickname(result.resolvedContext.patientNickname);
        }
      } catch (requestError) {
        if (controller.signal.aborted) return;
        if (
          effectiveScopeMode === "team" &&
          requestError instanceof ClinicianPortalApiError &&
          requestError.code === "FORBIDDEN"
        ) {
          setError(
            "Team assistant access is available only to authorized supervisors.",
          );
          setTeamAccessForbidden(true);
          setFailedRequest(null);
          return;
        }
        setError(friendlyAssistantError(requestError));
        setFailedRequest({
          question: normalizedQuestion,
          context: requestContext,
          appendUser: false,
        });
      } finally {
        if (requestController.current === controller) {
          requestController.current = undefined;
          setIsSubmitting(false);
        }
      }
    },
    [
      assistantScope,
      clinicianCode,
      context,
      effectiveScopeMode,
      isSubmitting,
      messages,
      requestingTherapist?.id,
    ],
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void sendQuestion(draft);
  };

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void sendQuestion(draft);
    }
  };

  const selectExampleQuestion = (question: string) => {
    const needsClientNickname = question.includes(clientNicknamePlaceholder);
    shouldSelectClientNicknamePlaceholder.current = needsClientNickname;
    setDraft(question);
    setShowExamples(false);
    if (!needsClientNickname) {
      composerRef.current?.focus();
    } else if (question === draft) {
      shouldSelectClientNicknamePlaceholder.current = false;
      const placeholderStart = question.indexOf(clientNicknamePlaceholder);
      composerRef.current?.focus();
      composerRef.current?.setSelectionRange(
        placeholderStart,
        placeholderStart + clientNicknamePlaceholder.length,
      );
    }
  };

  const selectScopeMode = (mode: ClinicianAssistantScope["mode"]) => {
    if (mode === "team" && !isSupervisor) return;
    hasExplicitScopeSelection.current = true;
    setScopeMode(mode);
  };

  const controllerValue = useMemo<ClinicianAssistantController>(
    () => ({
      open: () => setIsOpen(true),
      triggerRef,
      setTeamScopeFilters: (filters) =>
        setTeamScopeFilters((current) =>
          current.clinicId === filters.clinicId &&
          current.therapistId === filters.therapistId
            ? current
            : filters,
        ),
    }),
    [],
  );

  return (
    <ClinicianAssistantControllerContext.Provider value={controllerValue}>
      {children}
      {isOpen ? (
        <aside
          className="cp-clinician-assistant"
          role="dialog"
          aria-labelledby="clinician-assistant-heading"
        >
          <div className="cp-clinician-assistant__header">
            <div>
              <p className="cp-clinician-assistant__eyebrow">Clinical support</p>
              <div className="cp-clinician-assistant__title-row">
                <h2 id="clinician-assistant-heading">Clinician Assistant</h2>
                <span className="cp-clinician-assistant__experimental-badge">
                  Experimental
                </span>
              </div>
              {isSupervisor ? (
                <div
                  className="cp-clinician-assistant__scope-selector"
                  role="group"
                  aria-label="Assistant scope"
                >
                  <button
                    type="button"
                    aria-pressed={effectiveScopeMode === "team"}
                    title="All clients in your authorized team, including your own caseload"
                    onClick={() => selectScopeMode("team")}
                  >
                    Full team
                  </button>
                  <button
                    type="button"
                    aria-pressed={effectiveScopeMode === "personal"}
                    title="Clients assigned directly to you"
                    onClick={() => selectScopeMode("personal")}
                  >
                    My caseload
                  </button>
                </div>
              ) : null}
              {contextLabel ? (
                <span className="cp-clinician-assistant__context">
                  {contextLabel}
                </span>
              ) : null}
            </div>
            <div className="cp-clinician-assistant__header-actions">
              <button
                type="button"
                className="cp-clinician-assistant__icon-button"
                onClick={clearConversation}
                aria-label="Start a new conversation"
                title="New conversation"
              >
                <RotateCcw aria-hidden="true" />
              </button>
              <button
                ref={closeRef}
                type="button"
                className="cp-clinician-assistant__icon-button"
                onClick={() => {
                  setShowExamples(false);
                  setIsOpen(false);
                  triggerRef.current?.focus();
                }}
                aria-label="Close Clinician Assistant"
              >
                <X aria-hidden="true" />
              </button>
            </div>
          </div>

          <div
            ref={conversationRef}
            className="cp-clinician-assistant__conversation"
            aria-live="polite"
          >
            {messages.length === 0 ? (
              <section className="cp-clinician-assistant__welcome">
                <Sparkles aria-hidden="true" />
                <h3>How can I help?</h3>
                <p>
                  Ask about {effectiveScopeMode === "team"
                    ? "your team"
                    : context.patientId
                      ? "this client"
                      : "your clients"}
                  {" "}using the records available in the portal.
                </p>
                <div className="cp-clinician-assistant__starters">
                  {starters.map((starter) => (
                    <button
                      key={starter}
                      type="button"
                      disabled={!identityResolved}
                      onClick={() => void sendQuestion(starter)}
                    >
                      {starter}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="cp-clinician-assistant__examples-toggle"
                  aria-expanded={showExamples}
                  aria-controls="clinician-assistant-examples"
                  onClick={() => setShowExamples((current) => !current)}
                >
                  {showExamples
                    ? "Hide example questions"
                    : "View example questions"}
                  <ChevronDown aria-hidden="true" />
                </button>
                {showExamples ? (
                  <div
                    id="clinician-assistant-examples"
                    className="cp-clinician-assistant__examples"
                    aria-label="Example questions"
                  >
                    {exampleQuestionGroups.map((group) => (
                      <section
                        key={group.title}
                        className="cp-clinician-assistant__example-group"
                      >
                        <h4>{group.title}</h4>
                        {group.description ? <p>{group.description}</p> : null}
                        <div className="cp-clinician-assistant__example-list">
                          {group.questions.map((question) => (
                            <button
                              key={question}
                              type="button"
                              onClick={() => selectExampleQuestion(question)}
                            >
                              {question}
                            </button>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}

            {messages.map((message) => {
              const result = message.result;
              const isClarification = Boolean(
                result &&
                  "needsClarification" in result &&
                  result.needsClarification,
              );
              return (
                <article
                  key={message.id}
                  className={`cp-clinician-assistant__message cp-clinician-assistant__message--${message.role}`}
                >
                  <span className="cp-visually-hidden">
                    {message.role === "user" ? "You" : "Assistant"}:
                  </span>
                  <AssistantMarkdown>{message.content}</AssistantMarkdown>
                  {isClarification && result && "choices" in result ? (
                    <div className="cp-clinician-assistant__choices">
                      {result.choices.map((choice) => (
                        <button
                          key={choice.patientId}
                          type="button"
                          onClick={() =>
                            void sendQuestion(
                              message.originalQuestion ?? message.content,
                              {
                                ...context,
                                patientId: choice.patientId,
                                patientNickname: choice.label,
                              },
                              false,
                            )
                          }
                        >
                          {choice.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {result && !("needsClarification" in result && result.needsClarification) ? (
                    <>
                      {result.evidence?.length ? (
                        <ul className="cp-clinician-assistant__evidence" aria-label="Evidence">
                          {result.evidence.map((evidence, index) => (
                            <EvidenceCard
                              key={`${evidence.type}-${evidence.label}-${index}`}
                              evidence={evidence}
                            />
                          ))}
                        </ul>
                      ) : null}
                      {result.links?.length ? (
                        <div className="cp-clinician-assistant__links">
                          {result.links.map((link, index) => (
                            <StructuredLink
                              key={`${link.page}-${link.label}-${index}`}
                              link={link}
                              route={route}
                              clinicianCode={clinicianCode}
                            />
                          ))}
                        </div>
                      ) : null}
                      <p className="cp-clinician-assistant__timestamp">
                        As of {formatPortalDateTime(result.generatedAt)}
                      </p>
                    </>
                  ) : null}
                </article>
              );
            })}

            {isSubmitting ? (
              <div
                className="cp-clinician-assistant__message cp-clinician-assistant__message--assistant cp-clinician-assistant__loading"
                role="status"
              >
                <span>Reviewing portal records</span>
                <span
                  className="cp-clinician-assistant__loading-dots"
                  aria-hidden="true"
                >
                  <span
                    className="cp-clinician-assistant__loading-dot"
                    data-testid="clinician-assistant-loading-dot"
                  />
                  <span
                    className="cp-clinician-assistant__loading-dot"
                    data-testid="clinician-assistant-loading-dot"
                  />
                  <span
                    className="cp-clinician-assistant__loading-dot"
                    data-testid="clinician-assistant-loading-dot"
                  />
                </span>
              </div>
            ) : null}

            {error ? (
              <div className="cp-clinician-assistant__error" role="alert">
                <p>{error}</p>
                {failedRequest ? (
                  <button
                    type="button"
                    onClick={() =>
                      void sendQuestion(
                        failedRequest.question,
                        failedRequest.context,
                        failedRequest.appendUser,
                      )
                    }
                  >
                    Retry
                  </button>
                ) : null}
                {teamAccessForbidden ? (
                  <button
                    type="button"
                    onClick={() => selectScopeMode("personal")}
                  >
                    Switch to My caseload
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="cp-clinician-assistant__composer-wrap">
            <form className="cp-clinician-assistant__composer" onSubmit={handleSubmit}>
              <label className="cp-visually-hidden" htmlFor="clinician-assistant-question">
                Ask the Clinician Assistant
              </label>
              <textarea
                ref={composerRef}
                id="clinician-assistant-question"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleComposerKeyDown}
                aria-describedby={
                  draftNeedsClientNickname
                    ? "clinician-assistant-client-placeholder-warning"
                    : undefined
                }
                aria-invalid={draftNeedsClientNickname || undefined}
                placeholder="Ask about clients, homework, or recent activity…"
                rows={2}
                disabled={isSubmitting}
              />
              <button
                type="submit"
                disabled={
                  !identityResolved ||
                  isSubmitting ||
                  !draft.trim() ||
                  draftNeedsClientNickname
                }
                aria-label="Send question"
              >
                <Send aria-hidden="true" />
              </button>
            </form>
            {draftNeedsClientNickname ? (
              <p
                id="clinician-assistant-client-placeholder-warning"
                className="cp-clinician-assistant__composer-warning"
                role="alert"
              >
                Replace [client nickname] with the client&apos;s actual nickname
                before sending.
              </p>
            ) : null}
            <p className="cp-clinician-assistant__disclaimer">
              AI-generated summary. Verify important clinical decisions against the
              underlying records.
            </p>
          </div>
        </aside>
      ) : null}
    </ClinicianAssistantControllerContext.Provider>
  );
}
